import { useMemo, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectMetadataTypesList } from '../../../reducers/documentation/metadataReducer';
import { selectTopBarSearchText } from '../../../reducers/documentation/documentationTopBarReducer';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { DocumentationFiltersTypesMap } from '../selected-object/filters/filterTypes';
import { useSortElements } from '../selected-object/useSortElements';
import { getColumns } from '../selected-object/expanded-mode/tableColumns';
import { useDocumentationFiltersContext } from '../selected-object/filters/DocumentationFiltersContext';
import { ActiveState } from '../building-blocks/activity-state/helpers';
import { isItemActive } from '../helpers';
import { escapeRegexp } from '../../../lib/escapeRegex';

export const NO_NAMESPACE_VALUE = 'No namespace';

const createRobustFilters = () => {
  const validateAndNormalize = (values: any[]): Set<string> => {
    if (!Array.isArray(values)) {
      console.warn('Invalid filter values received, expected array:', values);
      return new Set();
    }

    return new Set(
      values
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v).trim().toLowerCase())
        .filter((v) => v.length > 0),
    );
  };

  const normalizeNamespace = (ns: any): string => {
    if (ns === null || ns === undefined || String(ns).trim() === '') {
      return NO_NAMESPACE_VALUE.toLowerCase();
    }
    return String(ns).trim().toLowerCase();
  };

  const safeGetFilterValues = (filterData: any): any[] => {
    try {
      if (!filterData || !Array.isArray(filterData.selectedValues)) {
        return [];
      }
      return filterData.selectedValues;
    } catch (error) {
      console.warn('Error accessing filter data:', error);
      return [];
    }
  };

  // Active state validation
  const isValidActiveState = (state: any): state is ActiveState => {
    return Object.values(ActiveState).includes(state);
  };

  return {
    validateAndNormalize,
    normalizeNamespace,
    safeGetFilterValues,
    isValidActiveState,
  };
};

export const useMetadataList = (crmOrgId: string, activeTab: string) => {
  const { filterValues } = useDocumentationFiltersContext();

  const { validateAndNormalize, normalizeNamespace, safeGetFilterValues, isValidActiveState } =
    createRobustFilters();

  const selectedNamespaceData = filterValues?.[DocumentationFiltersTypesMap.namespace];
  const selectedActiveStateData = filterValues?.[DocumentationFiltersTypesMap.activeState];

  const rawNamespaceValues = safeGetFilterValues(selectedNamespaceData);
  const rawActiveStateValues = safeGetFilterValues(selectedActiveStateData);

  const selectedActiveOnly = useMemo(() => {
    const activeState = rawActiveStateValues[0];
    return isValidActiveState(activeState) ? activeState === ActiveState.activeOnly : true; // Default fallback
  }, [rawActiveStateValues, isValidActiveState]);

  const metadataTypesList = useSelector(selectMetadataTypesList(crmOrgId));
  const metadataData = useMemo(() => {
    return metadataTypesList?.[activeTab];
  }, [metadataTypesList, activeTab]);

  const searchStr = useSelector(selectTopBarSearchText);
  const [debouncedSearchStr, setDebouncedSearchStr] = useState(searchStr || '');
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounce search string changes to prevent excessive filtering during typing
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchStr(searchStr || '');
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchStr]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const metadataColumns = useMemo(() => {
    return getColumns(DocumentationTabTypes.OTHER);
  }, []);

  const validatedNamespaceSet = useMemo(() => {
    return validateAndNormalize(rawNamespaceValues);
  }, [rawNamespaceValues, validateAndNormalize]);

  const hasNamespaceFilter = validatedNamespaceSet.size > 0;

  const itemSelector = useMemo(() => {
    return metadataColumns.map((column) => column.field);
  }, [metadataColumns]);

  const trimmedSearchStr = useMemo(() => {
    return debouncedSearchStr.trim();
  }, [debouncedSearchStr]);

  // Performance optimization: Two-stage filtering with debounced search
  // Stage 1 only recalculates when namespace/active filters change
  // Stage 2 only recalculates when debounced search text changes (300ms delay)
  const baseFilteredItems = useMemo(() => {
    if (!metadataData) return [];

    try {
      return metadataData.filter((item) => {
        const isSelectedNamespace = hasNamespaceFilter
          ? validatedNamespaceSet.has(normalizeNamespace(item.namespace))
          : true;

        const isActive = selectedActiveOnly ? isItemActive(item) : true;
        return isSelectedNamespace && isActive;
      });
    } catch (error) {
      console.error('Error in base filtering:', error);
      return metadataData;
    }
  }, [
    metadataData,
    hasNamespaceFilter,
    validatedNamespaceSet,
    normalizeNamespace,
    selectedActiveOnly,
  ]);

  const filteredItems = useMemo(() => {
    if (!trimmedSearchStr) return baseFilteredItems;

    try {
      // Get or create cached regex for search with error handling
      const searchRegex = new RegExp(escapeRegexp(trimmedSearchStr), 'i');

      return baseFilteredItems.filter((item) => {
        try {
          for (const prop of itemSelector) {
            // Safe property access with type checking
            const propValue = (item as any)[prop];
            if (typeof propValue === 'string' && searchRegex.test(propValue)) {
              return true;
            }
          }
          return false;
        } catch (itemError) {
          console.warn('Error filtering item:', item, itemError);
          return false;
        }
      });
    } catch (regexError) {
      console.error('Error creating search regex:', regexError);
      return baseFilteredItems;
    }
  }, [baseFilteredItems, trimmedSearchStr, itemSelector]);

  const { onSortChange, _sortOptions, sortedItems, _sortKey, sortOrder } = useSortElements({
    activeTab: DocumentationTabTypes.OTHER,
    isManagePackageInstalled: true,
    items: filteredItems,
  });

  return {
    items: sortedItems,
    onSortChange,
    _sortOptions,
    _sortKey,
    sortOrder,
    metadataColumns,
  };
};
